import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import gmailIntegrationApi, { IEmailDetail } from '~/apis/gmailIntegration';
import { notify } from '~/utils/helper';
import PrimaryInput from '~/components/common/primary-input/PrimaryInput';
import BaseTextarea from '~/components/common/base-textarea/BaseTextarea';
import BaseModal from '~/components/common/base-modal/BaseModal';
import styles from './EmailDetailModal.module.scss';
import classNames from 'classnames/bind';

const cx = classNames.bind(styles);

interface EmailDetailPageProps {
  messageId: string;
  jobId?: string;
  onBack: () => void;
}

const EmailDetailPage: React.FC<EmailDetailPageProps> = ({
  messageId,
  jobId,
  onBack,
}) => {
  const { t } = useTranslation(['admin/settings']);
  const [emailDetail, setEmailDetail] = useState<IEmailDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [replyTo, setReplyTo] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [forwardTo, setForwardTo] = useState('');
  const [forwardSubject, setForwardSubject] = useState('');
  const [forwardBody, setForwardBody] = useState('');
  const [sending, setSending] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState<File[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const replyFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (messageId) {
      fetchEmailDetail();
    } else {
      setEmailDetail(null);
      setError(null);
    }
  }, [messageId]);

  const fetchEmailDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await gmailIntegrationApi.getMessageDetail(messageId);
      if (response.data?.data) {
        setEmailDetail(response.data.data);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || t('mailbox.fetchDetailError');
      setError(errorMessage);
      notify('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getHeaderValue = (name: string): string => {
    if (!emailDetail?.payload?.headers) return '';
    const header = emailDetail.payload.headers.find((h) => h.name.toLowerCase() === name.toLowerCase());
    return header?.value || '';
  };

  const decodeBody = (body: string): string => {
    try {
      // Gmail API returns base64url encoded body
      // Replace - with + and _ with / for base64 decoding
      const base64 = body.replace(/-/g, '+').replace(/_/g, '/');
      return decodeURIComponent(escape(atob(base64)));
    } catch (error) {
      return body;
    }
  };

  const htmlToPlainText = (html: string): string => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const getEmailBody = (): string => {
    if (!emailDetail?.payload) return '';

    // Check if body has data
    if (emailDetail.payload.body?.data) {
      return decodeBody(emailDetail.payload.body.data);
    }

    // Check if payload has parts (multipart message)
    if (emailDetail.payload.parts) {
      for (const part of emailDetail.payload.parts) {
        if (part.body?.data) {
          return decodeBody(part.body.data);
        }
      }
    }

    return '';
  };

  const getMessageId = (): string => {
    if (!emailDetail?.payload?.headers) return '';
    const header = emailDetail.payload.headers.find(
      (h) => h.name.toLowerCase() === 'message-id'
    );
    return header?.value || '';
  };

  const handleReply = () => {
    const from = getHeaderValue('From');
    const subject = getHeaderValue('Subject');
    const date = getHeaderValue('Date');
    const originalBody = getEmailBody();
    const plainTextBody = htmlToPlainText(originalBody);

    setReplyTo(from);
    setReplySubject(subject.startsWith('Re:') ? subject : `Re: ${subject}`);
    setReplyBody(`\n\nOn ${date}, ${from} wrote:\n${plainTextBody.split('\n').map(line => `> ${line}`).join('\n')}`);
    setReplyAttachments([]);
    setReplyOpen(true);
  };

  const formatFileSize = (size: number): string => {
    if (size >= 1024 ** 3) return `${(size / 1024 ** 3).toFixed(2)} GB`;
    if (size >= 1024 ** 2) return `${(size / 1024 ** 2).toFixed(2)} MB`;
    if (size >= 1024) return `${(size / 1024).toFixed(2)} KB`;
    return `${size} B`;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles: File[] = [];

    files.forEach((file) => {
      if (file.size > 25 * 1024 * 1024) {
        notify('error', `File ${file.name} exceeds 25MB limit`);
        return;
      }
      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      setReplyAttachments((prev) => [...prev, ...validFiles]);
    }

    // Reset input
    if (replyFileInputRef.current) {
      replyFileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    const files = Array.from(e.dataTransfer.files || []);
    const validFiles: File[] = [];

    files.forEach((file) => {
      if (file.size > 25 * 1024 * 1024) {
        notify('error', `File ${file.name} exceeds 25MB limit`);
        return;
      }
      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      setReplyAttachments((prev) => [...prev, ...validFiles]);
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setReplyAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const openFileDialog = () => {
    replyFileInputRef.current?.click();
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = (reader.result as string).split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const getMimeType = (file: File): string => {
    return file.type || 'application/octet-stream';
  };

  const handleForward = () => {
    const from = getHeaderValue('From');
    const subject = getHeaderValue('Subject');
    const date = getHeaderValue('Date');
    const originalBody = getEmailBody();
    const plainTextBody = htmlToPlainText(originalBody);

    setForwardTo('');
    setForwardSubject(subject.startsWith('Fwd:') ? subject : `Fwd: ${subject}`);
    setForwardBody(`---------- Forwarded message ---------\nFrom: ${from}\nDate: ${date}\nSubject: ${subject}\n\n${plainTextBody}`);
    setForwardOpen(true);
  };

  const handleSendReply = async () => {
    if (!replyTo || !replyBody.trim()) {
      notify('error', t('mailbox.replyRequiredFields'));
      return;
    }

    setSending(true);
    try {
      const messageId = getMessageId();

      // Prepare attachments if files exist
      const attachments = replyAttachments.length > 0
        ? await Promise.all(
          replyAttachments.map(async (file) => ({
            filename: file.name,
            content: await fileToBase64(file),
            mimeType: getMimeType(file),
          }))
        )
        : undefined;

      await gmailIntegrationApi.sendEmail({
        to: replyTo,
        subject: replySubject,
        body: replyBody,
        inReplyTo: messageId,
        references: messageId,
        attachments,
        jobId,
      });
      notify('success', t('mailbox.replySuccess'));
      setReplyOpen(false);
      setReplyTo('');
      setReplySubject('');
      setReplyBody('');
      setReplyAttachments([]);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || t('mailbox.replyError');
      notify('error', errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleSendForward = async () => {
    if (!forwardTo || !forwardBody.trim()) {
      notify('error', t('mailbox.forwardRequiredFields'));
      return;
    }

    setSending(true);
    try {
      await gmailIntegrationApi.sendEmail({
        to: forwardTo,
        subject: forwardSubject,
        body: forwardBody,
        jobId,
      });
      notify('success', t('mailbox.forwardSuccess'));
      setForwardOpen(false);
      setForwardTo('');
      setForwardSubject('');
      setForwardBody('');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || t('mailbox.forwardError');
      notify('error', errorMessage);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Box className={cx('email-detail-page')}>
        {/* Header */}
        <Box className={cx('page-header')}>
          <Box className={cx('header-content')}>
            <Box className={cx('header-left')}>
              <IconButton
                onClick={onBack}
                className={cx('back-button')}
                size="small"
              >
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </IconButton>
              <Typography className={cx('page-title')}>
                {t('mailbox.emailDetail')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Content */}
        <Box className={cx('page-content')}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : emailDetail ? (
            <Box className={cx('content-wrapper')}>
              <Box className={styles.emailContent}>
                <Box className={styles.emailHeader}>
                  <Box className={styles.headerRow}>
                    <Typography className={styles.headerLabel}>{t('mailbox.from')}:</Typography>
                    <Typography className={styles.headerValue}>{getHeaderValue('From')}</Typography>
                  </Box>
                  <Box className={styles.headerRow}>
                    <Typography className={styles.headerLabel}>{t('mailbox.to')}:</Typography>
                    <Typography className={styles.headerValue}>{getHeaderValue('To')}</Typography>
                  </Box>
                  <Box className={styles.headerRow}>
                    <Typography className={styles.headerLabel}>{t('mailbox.subject')}:</Typography>
                    <Typography className={styles.headerValue}>{getHeaderValue('Subject')}</Typography>
                  </Box>
                  <Box className={styles.headerRow}>
                    <Typography className={styles.headerLabel}>{t('mailbox.date')}:</Typography>
                    <Typography className={styles.headerValue}>{getHeaderValue('Date')}</Typography>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: '#3A4A5C', my: 2 }} />

                <Box className={styles.emailBody}>
                  <Typography className={styles.bodyLabel}>{t('mailbox.content')}:</Typography>
                  <Box
                    className={styles.bodyContent}
                    dangerouslySetInnerHTML={{ __html: getEmailBody() }}
                  />
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>

        {/* Footer */}
        <Box className={cx('page-footer')}>
          <Box className={cx('modal-actions')}>
            <Box
              className={cx('action-button')}
              onClick={handleReply}
              style={{ opacity: !emailDetail ? 0.5 : 1, cursor: !emailDetail ? 'not-allowed' : 'pointer' }}
            >
              {t('mailbox.reply')}
            </Box>
            <Box
              className={cx('action-button')}
              onClick={handleForward}
              style={{ opacity: !emailDetail ? 0.5 : 1, cursor: !emailDetail ? 'not-allowed' : 'pointer' }}
            >
              {t('mailbox.forward')}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Reply Dialog */}
      <BaseModal
        open={replyOpen}
        onClose={() => {
          if (!sending) {
            setReplyOpen(false);
            setReplyAttachments([]);
          }
        }}
        width="1200px"
      >
        <Box className={cx('email-detail-modal')}>
          <Box className={cx('modal-header')}>
            <Box className={cx('header-content')}>
              <Typography className={cx('modal-title')}>
                {t('mailbox.reply')}
              </Typography>
              <IconButton
                onClick={() => setReplyOpen(false)}
                className={cx('close-button')}
                size="small"
                disabled={sending}
              >
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </IconButton>
            </Box>
          </Box>

          <Box className={cx('modal-content')}>
            <Box className={cx('content-wrapper')}>
              <Box className={styles.emailForm}>
                <PrimaryInput
                  label={t('mailbox.to')}
                  name="replyTo"
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                  disabled={sending}
                  placeholder={t('mailbox.to')}
                  className={styles.primaryInput}
                />
                <PrimaryInput
                  label={t('mailbox.subject')}
                  name="replySubject"
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                  disabled={sending}
                  placeholder={t('mailbox.subject')}
                  className={styles.primaryInput}
                />
                <BaseTextarea
                  type="textarea"
                  label={t('mailbox.body')}
                  placeholder={t('mailbox.body')}
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  disable={sending}
                  inputType="textarea"
                />

                {/* Attachment Section */}
                <Box className={styles.attachmentSection}>
                  <Typography className={styles.attachmentLabel}>
                    {t('mailbox.attachments') || 'Attachments'}
                  </Typography>
                  <Box
                    className={cx(styles.uploadArea, { [styles.dragActive]: isDragActive })}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    {replyAttachments.length > 0 ? (
                      <Box className={styles.attachmentsList}>
                        {replyAttachments.map((file, index) => (
                          <Box key={index} className={styles.attachmentItem}>
                            <Box className={styles.fileInfo}>
                              <Typography className={styles.fileName}>{file.name}</Typography>
                              <Typography className={styles.fileSize}>{formatFileSize(file.size)}</Typography>
                            </Box>
                            <IconButton
                              onClick={() => handleRemoveAttachment(index)}
                              className={styles.removeButton}
                              size="small"
                              disabled={sending}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </IconButton>
                          </Box>
                        ))}
                        <Box
                          className={styles.addMoreButton}
                          onClick={openFileDialog}
                          style={{ cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.5 : 1 }}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          <Typography className={styles.addMoreText}>{t('mailbox.addMoreFiles') || 'Add more files'}</Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box className={styles.uploadPlaceholder}>
                        <Typography className={styles.uploadText}>
                          {'Drag and drop your files here or'}{' '}
                          <span className={styles.clickUpload} onClick={openFileDialog}>
                            {'click to upload'}
                          </span>
                        </Typography>
                        <Typography className={styles.uploadDescription}>
                          {'Maximum file size: 25MB'}
                        </Typography>
                      </Box>
                    )}
                    <input
                      ref={replyFileInputRef}
                      type="file"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                      disabled={sending}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box className={cx('modal-footer')}>
            <Box className={cx('modal-actions')}>
              <Box className={cx('cancel-button')} onClick={() => setReplyOpen(false)}>
                {t('mailbox.cancel')}
              </Box>
              <Box
                className={cx('submit-button', { disabled: sending })}
                onClick={handleSendReply}
              >
                {sending ? <CircularProgress size={20} /> : t('mailbox.send')}
              </Box>
            </Box>
          </Box>
        </Box>
      </BaseModal>

      {/* Forward Dialog */}
      <BaseModal open={forwardOpen} onClose={() => !sending && setForwardOpen(false)} width="1200px">
        <Box className={cx('email-detail-modal')}>
          <Box className={cx('modal-header')}>
            <Box className={cx('header-content')}>
              <Typography className={cx('modal-title')}>
                {t('mailbox.forward')}
              </Typography>
              <IconButton
                onClick={() => setForwardOpen(false)}
                className={cx('close-button')}
                size="small"
                disabled={sending}
              >
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </IconButton>
            </Box>
          </Box>

          <Box className={cx('modal-content')}>
            <Box className={cx('content-wrapper')}>
              <Box className={styles.emailForm}>
                <PrimaryInput
                  label={t('mailbox.to')}
                  name="forwardTo"
                  value={forwardTo}
                  onChange={(e) => setForwardTo(e.target.value)}
                  disabled={sending}
                  placeholder={t('mailbox.to')}
                  className={styles.primaryInput}
                />
                <PrimaryInput
                  label={t('mailbox.subject')}
                  name="forwardSubject"
                  value={forwardSubject}
                  onChange={(e) => setForwardSubject(e.target.value)}
                  disabled={sending}
                  placeholder={t('mailbox.subject')}
                  className={styles.primaryInput}
                />
                <BaseTextarea
                  type="textarea"
                  label={t('mailbox.body')}
                  placeholder={t('mailbox.body')}
                  value={forwardBody}
                  onChange={(e) => setForwardBody(e.target.value)}
                  disable={sending}
                  inputType="textarea"
                />
              </Box>
            </Box>
          </Box>

          <Box className={cx('modal-footer')}>
            <Box className={cx('modal-actions')}>
              <Box className={cx('cancel-button')} onClick={() => setForwardOpen(false)}>
                {t('mailbox.cancel')}
              </Box>
              <Box
                className={cx('submit-button', { disabled: sending })}
                onClick={handleSendForward}
              >
                {sending ? <CircularProgress size={20} /> : t('mailbox.send')}
              </Box>
            </Box>
          </Box>
        </Box>
      </BaseModal>
    </>
  );
};

export default EmailDetailPage;

